import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faScissors, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { captureException } from '@sentry/angular';
import { ActivitiesGateway } from '@wilson/api/gateway';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  ActivityCategory,
  OperationStatus,
  ResolvedActivity,
  RoleAction,
  RoleIDsMap,
  RolePermissionSubject,
  ShiftState,
  ShiftWithActivitiesWithLocations,
  WilsonApp,
} from '@wilson/interfaces';
import { SplitActivityPopupComponent } from '@wilson/shared-components';
import { TimelineUpsertActivityV2 } from '@wilson/shift-timeline/state';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { firstValueFrom, map, Observable } from 'rxjs';
import { TimelineCommonOperationsService } from '../../../../services/timeline-common-operations.service';
import { AuthState } from '@wilson/auth/core';
import { DeleteAction } from '../timeline-activity-drawer.service';
import { ActivityDrawerDeleteForbiddenMessagePipe } from './activity-drawer-delete-forbidden-message.pipe';
import { AbilityServiceSignal } from '@casl/angular';

@Component({
  selector: 'wilson-activity-drawer-actions-section',
  imports: [
    CommonModule,
    FeatureFlagsModule,
    AuthorizationModule,
    NzButtonModule,
    TranslateModule,
    NzIconModule,
    NzAlertModule,
    NzTooltipModule,
    FontAwesomeModule,
    ActivityDrawerDeleteForbiddenMessagePipe,
  ],
  templateUrl: './activity-drawer-actions-section.component.html',
  styleUrls: ['./activity-drawer-actions-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityDrawerActionsSectionComponent implements OnInit {
  private readonly nzModalService = inject(NzModalService);
  private readonly translate = inject(TranslateService);
  private readonly activitiesGateway = inject(ActivitiesGateway);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly store = inject(Store);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translateService = inject(TranslateService);

  @Input() set shift(value: ShiftWithActivitiesWithLocations | null) {
    if (value) {
      this.isShiftStartedSignal.set(
        !value.activities.every(
          (activity) =>
            activity.operationalStatus === OperationStatus.NotStarted,
        ),
      );
      this.isShiftSubmittedSignal.set(value.state === ShiftState.Submitted);
    }
  }

  @Input()
  activity!: ResolvedActivity;

  @Input()
  activityCategories!: ActivityCategory[];

  @Input()
  isLastActivityInShift!: boolean;

  @Input()
  isAllDisabled!: boolean;

  @Input()
  activityUserId!: string | null;

  @Output()
  delete = new EventEmitter<DeleteAction>();

  @Output()
  closeDrawer = new EventEmitter();

  protected RoleAction = RoleAction;
  protected RolePermissionSubject = RolePermissionSubject;
  protected WilsonApp = WilsonApp;
  protected faTrashAlt = faTrashAlt;
  protected faScissors = faScissors;
  protected isAdminUser$!: Observable<boolean | undefined>;
  protected isShiftStartedSignal = signal(false);
  protected isShiftSubmittedSignal = signal(false);
  protected can = inject(AbilityServiceSignal).can;

  ngOnInit(): void {
    this.isAdminUser$ = this.store.select(AuthState.userRoles).pipe(
      map((userRoles) => {
        return userRoles?.some(
          (userRole) => userRole.roleId === RoleIDsMap.CompanyAdmin,
        );
      }),
    );
  }

  public async openSplitActivityPopup(): Promise<void> {
    const modal = this.nzModalService.create<SplitActivityPopupComponent>({
      nzTitle: this.translate.instant('activity.split_activity'),
      nzContent: SplitActivityPopupComponent,
      nzCentered: true,
      nzWidth: '600px',
      nzFooter: null,
      nzData: {
        activity: this.activity,
        activityCategories: this.activityCategories,
        preFilledForm: true,
      },
    });
    const result = await firstValueFrom(modal.afterClose);
    if (result) {
      const existingActivity = result.existingActivity;
      try {
        await this.activitiesGateway.updateActivity({
          id: existingActivity.id,
          endDatetime: existingActivity.endDatetime,
          endLocationId: existingActivity.endLocationId,
        });
        const createdActivities = await firstValueFrom(
          this.activitiesGateway.createActivities(result.newActivities),
        );
        this.store.dispatch(
          new TimelineUpsertActivityV2(
            [existingActivity, ...createdActivities],
            this.activity.shiftId as string,
          ),
        );

        if (this.activityUserId) {
          this.timelineCommonOperationsService.updateUsersAssignmentsState([
            this.activityUserId,
          ]);
          this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers(
            [this.activityUserId],
          );
        }
        this.closeDrawer.emit();
      } catch (error) {
        captureException(error);
        this.nzMessageService.error(
          this.translateService.instant('general.something_went_wrong'),
        );
      }
    }
  }
}
